import { AlertTriangle } from 'lucide-react-native';
import { useTranslation } from '../contexts/TranslationContext';
import { colors } from '../theme/colors';
import { Box, HStack, Text, VStack } from './primitives';
import { Button } from './primitives/Button';
import { Sheet } from './primitives/Sheet';

interface ConfirmModalProps {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ConfirmModal({
  visible,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  onCancel,
  isLoading = false,
}: Readonly<ConfirmModalProps>) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.actions.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('common.actions.cancel');
  const isDestructive = variant === 'destructive';
  const iconColor = isDestructive ? colors.status.error.main : colors.status.warning.main;
  const iconBgColor = isDestructive ? colors.status.error.bg : colors.status.warning.bg;

  return (
    <Sheet visible={visible} onClose={onCancel} position="center" size="sm" hideClose>
      <VStack
        spacing="lg"
        accessibilityViewIsModal
        accessibilityRole={isDestructive ? 'alert' : undefined}
        accessibilityLabel={`${title}. ${message}`}
        accessibilityLiveRegion={isDestructive ? 'assertive' : 'polite'}
      >
        <HStack spacing="md" align="center">
          <Box backgroundColor={iconBgColor} borderRadius="full" padding="sm">
            <AlertTriangle
              size={24}
              color={iconColor}
              accessibilityRole="image"
              accessibilityLabel={t('components.confirmModal.warningIconLabel')}
            />
          </Box>
          <Text
            variant="h3"
            color={colors.text.primary}
            style={{ flex: 1 }}
            accessibilityRole="header"
          >
            {title}
          </Text>
        </HStack>

        <Text variant="body" color={colors.text.secondary}>
          {message}
        </Text>

        <HStack spacing="md" justify="end">
          <Button
            variant="ghost"
            onPress={onCancel}
            disabled={isLoading}
            accessibilityLabel={resolvedCancelLabel}
            accessibilityState={{ disabled: isLoading }}
          >
            <Text variant="body" color={colors.text.secondary}>
              {resolvedCancelLabel}
            </Text>
          </Button>
          <Button
            variant={isDestructive ? 'secondary' : 'primary'}
            onPress={onConfirm}
            disabled={isLoading}
            accessibilityLabel={resolvedConfirmLabel}
            accessibilityHint={isDestructive ? message : undefined}
            accessibilityState={{ disabled: isLoading }}
            style={isDestructive ? { backgroundColor: colors.status.error.main } : undefined}
          >
            <Text
              variant="body"
              color={isDestructive ? colors.text.primary : colors.primary[900]}
              style={{ fontWeight: '600' }}
            >
              {isLoading ? t('components.confirmModal.processing') : resolvedConfirmLabel}
            </Text>
          </Button>
        </HStack>
      </VStack>
    </Sheet>
  );
}
